import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Library, Filter } from "lucide-react";
import { toast } from "sonner";
import { fetchArchive } from "@/lib/api";

const DIFFICULTY_STYLES = {
  easy: "bg-emerald-50 text-emerald-800 border-emerald-200",
  medium: "bg-slate-50 text-slate-700 border-slate-200",
  hard: "bg-red-50 text-red-800 border-red-200",
};

export default function Archive() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [difficulties, setDifficulties] = useState([]);
  const [specialty, setSpecialty] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await fetchArchive({ specialty, difficulty });
        if (cancelled) return;
        setItems(data.items || []);
        // Only refresh the filter sets on first load (unfiltered) so options don't shrink
        if (!specialty && !difficulty) {
          setSpecialties(data.specialties || []);
          setDifficulties(data.difficulties || []);
        }
      } catch (e) {
        toast.error("Could not load archive");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [specialty, difficulty]);

  const empty = useMemo(() => !loading && items.length === 0, [loading, items]);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-3xl mx-auto flex items-center justify-between px-4 py-3">
          <button
            data-testid="archive-back-btn"
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-slate-900 active:scale-95 transition-all"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <div className="flex items-center gap-2">
            <Library className="h-5 w-5 text-slate-900" />
            <span
              className="text-lg font-bold tracking-tight"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Archive
            </span>
          </div>
          <span className="w-14" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 md:py-10 space-y-6">
        <div>
          <p className="text-[11px] font-mono tracking-[0.2em] uppercase text-slate-500">
            Past cases
          </p>
          <h1
            className="text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900 mt-1"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Replay any case.
          </h1>
          <p className="text-sm text-slate-600 mt-2">
            Filter by specialty or difficulty. Archive plays don&apos;t count toward your streak.
          </p>
        </div>

        {/* Filters */}
        <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
          <div className="flex items-center gap-2 text-slate-700">
            <Filter className="h-4 w-4" />
            <span className="text-[11px] font-mono tracking-[0.2em] uppercase text-slate-500">
              Filter
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            <FilterChip
              testid="filter-specialty-all"
              active={!specialty}
              label="All specialties"
              onClick={() => setSpecialty("")}
            />
            {specialties.map((s) => (
              <FilterChip
                key={s}
                testid={`filter-specialty-${s.toLowerCase().replace(/\W+/g, "-")}`}
                active={specialty === s}
                label={s}
                onClick={() => setSpecialty(specialty === s ? "" : s)}
              />
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <FilterChip
              testid="filter-difficulty-all"
              active={!difficulty}
              label="All difficulties"
              onClick={() => setDifficulty("")}
            />
            {(difficulties.length ? difficulties : ["easy", "medium", "hard"]).map((d) => (
              <FilterChip
                key={d}
                testid={`filter-difficulty-${d}`}
                active={difficulty === d}
                label={d.charAt(0).toUpperCase() + d.slice(1)}
                onClick={() => setDifficulty(difficulty === d ? "" : d)}
              />
            ))}
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="text-slate-500 text-sm font-mono tracking-[0.2em] uppercase">
            Loading…
          </div>
        ) : empty ? (
          <div
            data-testid="archive-empty"
            className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-slate-500"
          >
            No cases match this filter yet.
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {items.map((it, idx) => (
              <motion.button
                key={it.case_id}
                data-testid={`archive-item-${it.case_id}`}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.02, duration: 0.25 }}
                onClick={() => navigate(`/play/${it.case_id}`)}
                className="text-left rounded-2xl border border-slate-200 bg-white p-5 hover:border-slate-400 hover:-translate-y-0.5 active:scale-[0.99] transition-all shadow-[0_2px_8px_rgba(15,23,42,0.03)]"
              >
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="text-[11px] font-mono tracking-[0.2em] uppercase text-slate-500">
                    {it.date}
                  </span>
                  <span
                    className={`text-[10px] font-mono tracking-widest uppercase border rounded-full px-2 py-0.5 ${
                      DIFFICULTY_STYLES[it.difficulty] || DIFFICULTY_STYLES.medium
                    }`}
                  >
                    {it.difficulty}
                  </span>
                </div>
                <p
                  className="text-sm font-semibold text-slate-900"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                >
                  {it.specialty}
                </p>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed line-clamp-3">
                  {it.preview}
                </p>
              </motion.button>
            ))}
          </div>
        )}

        <p className="text-center text-xs text-slate-400 pt-4 pb-2">
          New case daily. Old cases live here forever.
        </p>
      </main>
    </div>
  );
}

function FilterChip({ active, label, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-all active:scale-95 ${
        active
          ? "bg-slate-900 border-slate-900 text-white"
          : "bg-white border-slate-300 text-slate-700 hover:border-slate-500"
      }`}
    >
      {label}
    </button>
  );
}
